import { DialogComponent, DialogOpenEvent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";

/**
 * Escapes a string for safe use as HTML text content.
 *
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Escapes a string for safe use inside an HTML attribute value (assumes
 * double-quoted context).
 *
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #pendingFallbackTimer;

  /** @type {MutationObserver | undefined} */
  #pendingMorphObserver;

  /**
   * Tracks whether we temporarily stripped `cart-drawer--empty` from the
   * dialog while pending so we know to restore it on cancel (e.g. add error
   * from an empty cart leaves the drawer genuinely empty again).
   * @type {boolean}
   */
  #pendingStrippedEmptyClass = false;

  /**
   * The optimistic line-item skeleton injected during openOptimistic, kept
   * here so we can remove it on morph completion / cancel / re-open.
   * @type {HTMLElement | undefined}
   */
  #optimisticRow;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#pendingMorphObserver?.disconnect();
    this.#pendingMorphObserver = undefined;
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState,
    );
  }

  #handleCartAdd = (/** @type {CartAddEvent} */ event) => {
    this.#trackItemCount(event);

    // CartAddEvent and CartUpdateEvent share the same event name
    // (`ThemeEvents.cartUpdate` === "cart:update"), so this listener also
    // fires on removes / quantity updates. Filter by class identity so we
    // only auto-open on real adds — otherwise a slow section render landing
    // *after* the user clicks close (e.g. removing the last item) re-opens
    // the drawer because `showDialog()` gets called below.
    if (!(event instanceof CartAddEvent)) return;

    // On error, just clear the pending state.
    if (event?.detail?.data?.didError) {
      this.cancelOptimistic();
      return;
    }

    if (this.hasAttribute("auto-open")) {
      this.showDialog();
    }

    // Clear pending only after the cart-items section actually morphs into
    // the DOM (otherwise the empty-state markup is still present and flashes
    // through once `data-pending-add` is removed).
    //
    // - Inline sections path (`sections=` on /cart/add.js): the morph runs
    //   synchronously in this task, so a MutationObserver will fire on the
    //   next microtask/frame.
    // - Async path (the perf default): component-cart-items fetches and
    //   morphs the section a few hundred ms later — the observer waits.
    // Safety timeout guarantees we never leave the bar spinning forever.
    this.#watchForCartItemsMorph();
  };

  /**
   * Keeps `data-item-count` current so open() knows whether the cart is
   * empty. Product-form events carry a delta (the quantity just added); every
   * other cart update carries the new total — the same rule cart-icon.js uses.
   *
   * @param {Event} event
   */
  #trackItemCount(event) {
    const data = /** @type {any} */ (event).detail?.data;
    const itemCount = data?.itemCount;
    if (data?.didError || typeof itemCount !== "number") return;

    const current = Number(this.dataset.itemCount) || 0;
    this.dataset.itemCount = String(
      data.source === "product-form-component"
        ? current + itemCount
        : itemCount,
    );
  }

  /**
   * Observes the cart-items-component subtree for the first DOM mutation
   * (= the morph landed). Clears pending after the new content paints.
   */
  #watchForCartItemsMorph() {
    const target =
      this.querySelector("cart-items-component") ??
      this.refs?.dialog?.querySelector("cart-items-component");

    clearTimeout(this.#pendingFallbackTimer);
    this.#pendingMorphObserver?.disconnect();

    const clearPending = () => {
      this.#pendingMorphObserver?.disconnect();
      this.#pendingMorphObserver = undefined;
      clearTimeout(this.#pendingFallbackTimer);
      // Double rAF: wait for the morph mutation batch AND its paint frame
      // before fading the pending bar/dim out, so users never see the old
      // empty markup uncovered.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.removeAttribute("data-pending-add");
          this.#pendingStrippedEmptyClass = false;
          // Fade the optimistic skeleton out so it cross-fades with the
          // real row that just morphed in behind it.
          this.#removeOptimisticRow();
        });
      });
    };

    if (target) {
      this.#pendingMorphObserver = new MutationObserver(clearPending);
      this.#pendingMorphObserver.observe(target, {
        childList: true,
        subtree: true,
      });
    }

    // Safety fallback in case the morph never lands (network failure, etc.).
    this.#pendingFallbackTimer = window.setTimeout(clearPending, 4000);
  }

  /**
   * Whether auto-open is enabled for this drawer.
   * @returns {boolean}
   */
  get autoOpenEnabled() {
    return this.hasAttribute("auto-open");
  }

  /**
   * Opens the drawer immediately and marks it as pending-add. Also injects
   * an optimistic line-item skeleton (image + title) so the user sees their
   * product appear in the drawer instantly instead of waiting ~1.1s for the
   * server-rendered section to land.
   *
   * The skeleton is appended as a sibling of `.cart-drawer__inner` inside
   * the dialog, *outside* the `[data-hydration-key="cart-drawer-inner"]`
   * subtree, so the section morph (which runs in hydration mode for the
   * drawer) provably will not touch or replace it. It's removed by
   * #handleCartAdd with a short fade once the real morph lands.
   *
   * Duplicates: if `variantId` is already in the cart's existing rows
   * (Shopify will just bump quantity on that row), the skeleton is skipped
   * to avoid showing a second "copy" of the same product.
   *
   * Safe to call repeatedly.
   *
   * @param {Object} [opts]
   * @param {string} [opts.image] - Variant image URL to render in the skeleton.
   * @param {string} [opts.title] - Product title to render in the skeleton.
   * @param {string | number} [opts.variantId] - Variant id for duplicate detection.
   * @param {string} [opts.productUrl] - Product URL for the optimistic row title link.
   * @param {string} [opts.priceFormatted] - Localized price text (e.g. "$220.00").
   * @param {Array<{name: string, value: string}>} [opts.options] - Selected variant options.
   */
  openOptimistic(opts = {}) {
    if (!this.autoOpenEnabled) return;

    this.setAttribute("data-pending-add", "true");
    this.#stripEmptyLayoutClass();

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) {
      this.showDialog();
    }

    this.#injectOptimisticRow(opts);
    this.#updateOptimisticTotals(opts);
  }

  /**
   * Clears the pending-add state, e.g. on add error or network failure.
   */
  cancelOptimistic() {
    clearTimeout(this.#pendingFallbackTimer);
    this.#pendingMorphObserver?.disconnect();
    this.#pendingMorphObserver = undefined;
    this.removeAttribute("data-pending-add");
    this.#restoreEmptyLayoutClass();
    this.#removeOptimisticRow(true);
  }

  /**
   * Injects the optimistic line-item skeleton. No-op if the dialog isn't
   * ready, or if the variant is already a row in the existing cart (Shopify
   * will just bump quantity on that row — showing another card would mislead).
   *
   * The row is inserted as the first child of `.cart-items__wrapper` inside
   * the `[data-hydration-key]` boundary, so the section morph cleans it up
   * automatically when the real cart contents land.
   *
   * @param {{image?: string, title?: string, variantId?: string | number, productUrl?: string, priceFormatted?: string, options?: Array<{name: string, value: string}>}} opts
   */
  #injectOptimisticRow(opts) {
    const { image, title, variantId, productUrl, priceFormatted, options } =
      opts;
    const { dialog } = /** @type {Refs} */ (this.refs);

    this.#removeOptimisticRow(true);

    if (!dialog) return;

    if (variantId != null && this.#variantAlreadyInCart(dialog, variantId)) {
      return;
    }

    // Insert into the items wrapper so the row inherits all the CSS custom
    // properties cart-products sets there (media sizing, typography vars).
    // Falls back to the items scroll-hint if the wrapper isn't present.
    const wrapper =
      dialog.querySelector(".cart-items__wrapper") ??
      dialog.querySelector(".cart-drawer__items");
    if (!wrapper) return;

    const row = document.createElement("div");
    row.className = "cart-drawer__optimistic-row cart-items__table-row";

    const titleEl = productUrl
      ? `<a class="cart-items__title" href="${escapeAttr(productUrl)}" tabindex="-1">${escapeHtml(title || "")}</a>`
      : `<span class="cart-items__title">${escapeHtml(title || "")}</span>`;

    const optionsList =
      options && options.length
        ? `<dl class="cart-items__variants">${options
            .map(
              (o) =>
                `<div class="cart-items__variant"><dt>${escapeHtml(o.name)}:</dt><dd>${escapeHtml(o.value)}</dd></div>`,
            )
            .join("")}</dl>`
        : "";

    // Mirror the real cart row's media markup exactly:
    //   <a class="cart-items__media-container" style="--ratio:1.0;">
    //     <img class="cart-items__media-image border-style"
    //          width="250" height="250"
    //          style="--border-width:1px;--border-style:none;
    //                 --border-color:rgb(var(--color-border-rgb)/0.5);
    //                 --border-radius:0px;">
    // The `border-style` class + these custom-property values are what
    // `assets/base.css` consumes; matching them avoids a 1px border /
    // radius flicker at morph time. Width/height attributes give the
    // browser an intrinsic aspect ratio identical to the real image.
    const mediaImg = image
      ? `<img
          src="${escapeAttr(image)}"
          alt=""
          decoding="async"
          width="250"
          height="250"
          class="cart-items__media-image border-style"
          style="--border-width: 1px; --border-style: none; --border-color: rgb(var(--color-border-rgb) / 0.5); --border-radius: 0px;"
        >`
      : "";
    const mediaInner = image
      ? productUrl
        ? `<a class="cart-items__media-container" href="${escapeAttr(productUrl)}" tabindex="-1" style="--ratio:1.0;">${mediaImg}</a>`
        : `<div class="cart-items__media-container" style="--ratio:1.0;">${mediaImg}</div>`
      : "";

    // Real cart price renders inside `<text-component value="$X.XX">`,
    // which is `display: inline-block; line-height: 1` (see base.css).
    // Without this wrapper, the price column inherits the parent's
    // line-height and the row's price cell renders slightly taller.
    const priceMarkup = priceFormatted
      ? `<text-component value="${escapeAttr(priceFormatted)}">${escapeHtml(priceFormatted)}</text-component>`
      : "";

    row.innerHTML = `
      <div class="cart-items__media" role="cell">${mediaInner}</div>
      <div class="cart-items__details cart-primary-typography" role="cell">
        <p>${titleEl}</p>
        ${optionsList}
      </div>
      <div class="cart-items__quantity" role="cell">
        <div class="cart-items__quantity-controls" data-optimistic-qty-slot></div>
      </div>
      <div class="cart-items__price cart-secondary-typography" role="cell">
        ${priceMarkup}
      </div>
    `;

    // Clone the server-rendered quantity-selector template (real SVGs +
    // styling) into the quantity slot. Falls back gracefully if the template
    // isn't present (older cached pages).
    const qtySlot = row.querySelector("[data-optimistic-qty-slot]");
    const qtyTemplate = /** @type {HTMLTemplateElement | null} */ (
      document.getElementById("cart-drawer-optimistic-quantity-template")
    );
    if (qtySlot && qtyTemplate) {
      qtySlot.appendChild(qtyTemplate.content.cloneNode(true));
    }
    if (qtySlot) {
      // Mirror the real remove control markup
      // (`<button class="button cart-items__remove cart-items__remove--text" type="button">`)
      // so the `.button` class's `--button-font-size` / line-height /
      // border-radius cascade applies identically and we don't get a
      // height/baseline jump when the real cart row morphs in. The real
      // markup also ships a trailing `<span class="visually-hidden">` —
      // include it so the morph diff is purely an attribute swap.
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className =
        "button cart-items__remove cart-items__remove--text";
      removeBtn.setAttribute("tabindex", "-1");
      removeBtn.setAttribute("aria-hidden", "true");
      const removeLabel = this.#removeLabel();
      removeBtn.innerHTML =
        `<span class="cart-items__remove-label">${escapeHtml(removeLabel)}</span>` +
        `<span class="visually-hidden">${escapeHtml(removeLabel)}</span>`;
      qtySlot.appendChild(removeBtn);
    }

    wrapper.prepend(row);
    this.#optimisticRow = row;
  }

  /**
   * Updates the cart summary (estimated total) and free-shipping bar in
   * place so the optimistic state reflects the just-added item. Reads the
   * pre-add cart total + threshold from data attributes on the dialog /
   * shipping bar (rendered server-side from `cart.total_price`).
   *
   * Skips updates when we don't have a numeric price to add (best-effort,
   * the real morph will correct anything we miss).
   *
   * @param {{priceFormatted?: string}} opts
   */
  #updateOptimisticTotals(opts) {
    const { priceFormatted } = opts;
    if (!priceFormatted) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const priceCents = this.#parseMoneyToCents(priceFormatted);
    if (priceCents == null) return;

    const currentCartCents = Number(dialog.dataset.cartTotalCents || "0") || 0;
    const newCartCents = currentCartCents + priceCents;

    // Estimated total
    const totalValue = dialog.querySelector(".cart__total-value");
    if (totalValue) {
      const newTotal = this.#reformatMoney(priceFormatted, newCartCents);
      totalValue.textContent = newTotal;
      totalValue.setAttribute("value", newTotal);
    }

    // Free shipping bar
    const shippingBar = /** @type {HTMLElement | null} */ (
      dialog.querySelector("[data-free-shipping-bar]")
    );
    if (shippingBar) {
      const threshold = Number(shippingBar.dataset.thresholdCents || "0") || 0;
      if (threshold > 0) {
        const progress = Math.min(100, (newCartCents / threshold) * 100);
        const fill = /** @type {HTMLElement | null} */ (
          shippingBar.querySelector(".cart-drawer__shipping-fill")
        );
        if (fill) {
          fill.style.setProperty("--shipping-progress", `${progress}%`);
        }
        // Update the remaining amount text. Replace the first money-shaped
        // substring in the existing text with our recomputed value — keeps
        // localization (e.g. "$X away") without us having to know the
        // translation template.
        const textEl = shippingBar.querySelector(".cart-drawer__shipping-text");
        if (textEl) {
          if (newCartCents >= threshold) {
            // Eligible — leave the text alone so it can pick up the
            // localized "eligible" copy after morph.
          } else {
            const remainingCents = threshold - newCartCents;
            const remaining = this.#reformatMoney(
              priceFormatted,
              remainingCents,
            );
            const existing = textEl.textContent || "";
            const replaced = existing.replace(/\$?\d[\d,.]*/, remaining);
            if (replaced !== existing) textEl.textContent = replaced;
          }
        }
      }
    }
  }

  /**
   * Parses a localized money string (e.g. "$220.00", "€1.234,56") into
   * integer cents. Returns null if no number is found.
   *
   * @param {string} text
   * @returns {number | null}
   */
  #parseMoneyToCents(text) {
    // Match the FIRST money-shaped token only. Guards against price
    // strings that contain multiple numbers (e.g. a sale price plus the
    // compare-at price, or an a11y "Regular price $300 Sale price $220"
    // string), which would otherwise be smooshed together and parsed as
    // a gigantic number — pegging the free-shipping bar at 100%.
    const tokenMatch = text.match(/-?\d[\d.,]*/);
    if (!tokenMatch) return null;
    const cleaned = tokenMatch[0];
    if (!cleaned) return null;

    // Detect separator style: if last separator is comma w/ 2 trailing digits,
    // assume comma is decimal (European). Otherwise assume dot is decimal.
    const lastDot = cleaned.lastIndexOf(".");
    const lastComma = cleaned.lastIndexOf(",");
    let normalized;
    if (lastComma > lastDot) {
      // Comma is decimal separator.
      normalized = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // Dot is decimal (or no separator).
      normalized = cleaned.replace(/,/g, "");
    }
    const num = Number(normalized);
    if (!Number.isFinite(num)) return null;
    return Math.round(num * 100);
  }

  /**
   * Reformats `cents` using the same shape (currency symbol + separators)
   * as the given template string. Best-effort: extracts the leading/trailing
   * currency symbol/code from the template and applies the template's
   * decimal/thousands separator style.
   *
   * @param {string} template - A reference money string like "$220.00".
   * @param {number} cents
   * @returns {string}
   */
  #reformatMoney(template, cents) {
    // Identify symbol/code (anything that isn't digit/punctuation/space).
    const symbolMatch = template.match(/^[^\d.,\s-]+|[^\d.,\s-]+$/);
    const symbol = symbolMatch ? symbolMatch[0] : "";
    const symbolAtStart = template.trimStart().startsWith(symbol);

    // Detect separator style from template.
    const lastDot = template.lastIndexOf(".");
    const lastComma = template.lastIndexOf(",");
    const decimalSep = lastComma > lastDot ? "," : ".";
    const thousandsSep = decimalSep === "." ? "," : ".";

    // Detect decimals from template.
    const decimalsMatch = template.match(
      decimalSep === "." ? /\.(\d+)$/ : /,(\d+)$/,
    );
    const decimals = decimalsMatch?.[1]?.length ?? 2;

    const amount = cents / 100;
    const fixed = amount.toFixed(decimals);
    const [intPart = "0", decPart = ""] = fixed.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    const number =
      decimals > 0 ? `${intFormatted}${decimalSep}${decPart}` : intFormatted;

    if (!symbol) return number;
    // Preserve whitespace pattern (e.g. "€ 1,00" vs "$1.00").
    const hasSpace = / /.test(template);
    const sep = hasSpace ? " " : "";
    return symbolAtStart
      ? `${symbol}${sep}${number}`
      : `${number}${sep}${symbol}`;
  }

  /**
   * Returns the localized "Remove" label by reading it off an existing
   * remove button in the drawer (so we don't need to plumb a translation
   * to JS). Falls back to "Remove" if no such label is rendered yet.
   *
   * @returns {string}
   */
  #removeLabel() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const existing = dialog?.querySelector(".cart-items__remove-label");
    return existing?.textContent?.trim() || "Remove";
  }

  /**
   * Removes the optimistic row, fading out by default so it cross-fades with
   * the real row that just morphed in behind it. Passing `immediate=true`
   * skips the fade (used on cancel/error).
   *
   * @param {boolean} [immediate]
   */
  #removeOptimisticRow(immediate = false) {
    const row = this.#optimisticRow;
    if (!row) return;
    this.#optimisticRow = undefined;

    if (immediate) {
      row.remove();
      return;
    }

    row.classList.add("cart-drawer__optimistic-row--leaving");
    const cleanup = () => row.remove();
    row.addEventListener("transitionend", cleanup, { once: true });
    // Safety: ensure removal even if transitionend doesn't fire (e.g. prefers-reduced-motion).
    setTimeout(cleanup, 600);
  }

  /**
   * Checks whether the variant is already represented by a `data-key` row
   * in the current drawer. Shopify's line `key` format is `<variantId>:<hash>`,
   * with the hash absent when there are no line properties. We match either.
   *
   * @param {Element} dialog
   * @param {string | number} variantId
   * @returns {boolean}
   */
  #variantAlreadyInCart(dialog, variantId) {
    const id = String(variantId);
    // CSS.escape isn't strictly needed for numeric Shopify variant ids, but
    // play it safe in case a non-numeric id is ever passed.
    const safe = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
    return Boolean(
      dialog.querySelector(`[data-key="${safe}"]`) ||
        dialog.querySelector(`[data-key^="${safe}:"]`),
    );
  }

  /**
   * Removes the server-rendered `cart-drawer--empty` class from the dialog
   * while pending so the drawer doesn't render its centered/100dvh empty
   * layout in the click -> items-morph window. The class is restored by
   * #restoreEmptyLayoutClass on cancel; on success it stays off because the
   * morph will repopulate items and the cart is no longer empty.
   */
  #stripEmptyLayoutClass() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || !dialog.classList.contains("cart-drawer--empty")) return;
    dialog.classList.remove("cart-drawer--empty");
    this.#pendingStrippedEmptyClass = true;
  }

  /**
   * Restores `cart-drawer--empty` if we stripped it. Called from
   * cancelOptimistic so an add error from an empty cart doesn't leave the
   * drawer rendering as non-empty when it actually is.
   */
  #restoreEmptyLayoutClass() {
    if (!this.#pendingStrippedEmptyClass) return;
    const { dialog } = /** @type {Refs} */ (this.refs);
    dialog?.classList.add("cart-drawer--empty");
    this.#pendingStrippedEmptyClass = false;
  }

  open() {
    // The "When the cart is empty" theme setting can send an empty cart to
    // the cart page instead of opening an empty drawer.
    if (
      this.dataset.emptyCartAction === "page" &&
      Number(this.dataset.itemCount) === 0
    ) {
      window.location.assign(Theme.routes.cart_url);
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      "cart-summary-sticky",
      ratio > this.#summaryThreshold ? "false" : "true",
    );
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
